import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell,
  User,
  LogOut,
  Settings,
  LayoutDashboard,
  BookOpen,
  CreditCard,
  FileText,
  ChevronDown,
  Users,
  FileSpreadsheet,
  Heart,
  Receipt,
  UserCircle,
  Banknote,
  Wallet,
  Phone,
} from "lucide-react";
import PropTypes from "prop-types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getIcon = (iconName) => {
  const icons = {
    "layout-dashboard": LayoutDashboard,
    "book-open": BookOpen,
    "credit-card": CreditCard,
    users: Users,
    settings: Settings,
    "file-text": FileText,
    reports: FileSpreadsheet,
    bell: Bell,
    logout: LogOut,
    heart: Heart,
    receipt: Receipt,
    "user-circle": UserCircle,
    banknote: Banknote,
    wallet: Wallet,
    phone: Phone,
  };
  return icons[iconName] || LayoutDashboard;
};

export function DashboardShell({ children, navItems }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  return (
    <div className="relative flex min-h-screen flex-col bg-muted/30">
      {/* ── Desktop Top Navigation ── */}
      <header className="hidden md:block sticky top-0 z-40 w-full bg-background border-b border-border/50">
        <div className="max-w-7xl mx-auto flex h-12 items-center justify-between px-6">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-6 flex-1">
            <Link className="flex items-center gap-2 shrink-0" to="/">
              <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
                <span className="text-[11px] font-bold text-primary-foreground">
                  T
                </span>
              </div>
              <span className="text-base font-semibold tracking-tight">
                Taawun
              </span>
            </Link>

            <div className="h-5 w-px bg-border" />

            <nav className="flex items-center gap-0.5">
              {navItems.map((item, index) => {
                const Icon = getIcon(item.icon);
                const isActive =
                  pathname === item.href ||
                  pathname.startsWith(item.href + "/");
                return (
                  <Link
                    key={index}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/80",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{item.title}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground"
                >
                  <div className="h-6 w-6 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-primary">
                      {(user?.chapterName ||
                        user?.email ||
                        "?")?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs font-medium max-w-[120px] truncate">
                    {user?.chapterName || user?.email || "Account"}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-semibold">
                      {user?.chapterName || "Account"}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer text-xs gap-2">
                  <User className="h-3.5 w-3.5" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer text-xs gap-2">
                  <Settings className="h-3.5 w-3.5" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive text-xs gap-2"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 pb-20 md:pb-6">
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-5">
          {children}
        </div>
      </main>

      {/* ── Mobile Bottom Navigation ── */}
      <div className="block md:hidden fixed bottom-0 left-0 right-0 border-t border-border/50 bg-background/95 backdrop-blur-lg supports-[backdrop-filter]:bg-background/80">
        <nav className="flex justify-around items-end h-16 px-1 pb-[env(safe-area-inset-bottom)]">
          {navItems.map((item, index) => {
            const Icon = getIcon(item.icon);
            const isActive =
              pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <Link
                key={index}
                to={item.href}
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-0.5 pt-2 pb-1.5 relative transition-colors",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground active:text-foreground",
                )}
              >
                {isActive && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-5 rounded-full bg-primary" />
                )}
                <Icon
                  className={cn(
                    "h-[18px] w-[18px]",
                    isActive && "stroke-[2.5]",
                  )}
                />
                <span className="text-[10px] font-medium leading-tight text-center line-clamp-1">
                  {item.title}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

// Props validation
DashboardShell.propTypes = {
  children: PropTypes.node.isRequired,
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
